/**
 * Self-hosted Fontsource variable fonts for CJK text. Each package splits its font into
 * unicode-range pieces, so a page only downloads the pieces for the characters it shows.
 * Only CJK needs this, as its fonts have thousands of glyphs; scripts with an alphabet (e.g.
 * Arabic, Hebrew) are small enough to sit in LE-fonts.css with a unicode-range instead.
 *
 * These languages share Han characters but draw some of them differently. All the families
 * can sit in one font stack because a page only loads its own language's stylesheets.
 */
export const CJK_FONT_PACKAGES = {
  ja: ['noto-sans-jp', 'noto-serif-jp'],
  ko: ['noto-sans-kr', 'noto-serif-kr'],
  'zh-Hans': ['noto-sans-sc', 'noto-serif-sc'],
  'zh-Hant': ['noto-sans-tc', 'noto-serif-tc'],
} as const satisfies Record<string, ReadonlyArray<string>>

export type CjkFontLanguage = keyof typeof CJK_FONT_PACKAGES

const CJK_SANS_FONT_NAMES = ['Noto Sans JP Variable', 'Noto Sans KR Variable', 'Noto Sans SC Variable', 'Noto Sans TC Variable']
const CJK_SERIF_FONT_NAMES = ['Noto Serif JP Variable', 'Noto Serif KR Variable', 'Noto Serif SC Variable', 'Noto Serif TC Variable']

function toFontStack(fontNames: ReadonlyArray<string>): string {
  return fontNames.map(fontName => `"${fontName}"`).join(', ')
}

export const CJK_SANS_FONTS = toFontStack(CJK_SANS_FONT_NAMES)
export const CJK_SERIF_FONTS = toFontStack(CJK_SERIF_FONT_NAMES)

/**
 * Whether a font-family stack lists any CJK font, checked name by name as the browser would,
 * so it doesn't depend on how the stack is ordered or formatted.
 */
export function includesCjkFont(fontFamily: string): boolean {
  return [...CJK_SANS_FONT_NAMES, ...CJK_SERIF_FONT_NAMES].some(fontName => fontFamily.includes(fontName))
}

function isCjkFontLanguage(language: string): language is CjkFontLanguage {
  return Object.hasOwn(CJK_FONT_PACKAGES, language)
}

/**
 * The CJK fonts a locale needs, if any. Simplified and Traditional Chinese share the `zh`
 * language code, so they're told apart by script, e.g. zh-CN is Hans and zh-HK is Hant.
 */
export function getCjkFontLanguage(locale: string): CjkFontLanguage | undefined {
  const { language, script } = new Intl.Locale(locale.replace(/_/g, '-')).maximize()
  const fontLanguage = language === 'zh' ? `zh-${script}` : language
  return isCjkFontLanguage(fontLanguage) ? fontLanguage : undefined
}
